import React from 'react';
import { LegalLayout } from '@/components/layout/LegalLayout';

export default function PrivacyPolicy() {
  return (
    <LegalLayout title="Privacy Policy">
      <p className="mb-8 text-slate-500 dark:text-slate-400"><strong>Last updated:</strong> June 11, 2026</p>

      <p className="mb-8">
        ResumePro ("we," "us," or "our") is built and operated by Farhad Rezaei. This policy explains what data the application processes and how. Our core principle is <strong className="text-slate-900 dark:text-white font-semibold">local-first privacy</strong>: your resume content stays on your device, and our servers act only as a secure bridge to the AI that generates your results.
      </p>

      <section>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">1. Resume Content and Generated Materials</h2>
        <p className="mb-4">
          Your resume content, job descriptions, and generated materials are sensitive, and we treat them that way.
        </p>
        <ul className="space-y-3 mb-8">
          <li className="flex gap-3 items-start">
            <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <span><strong className="text-slate-900 dark:text-white font-semibold">We do not store, retain, or persist your resume content on our servers or in any database.</strong> Our backend acts only as a secure bridge that forwards your request to our AI provider and returns the result to your browser. No copy of your resume content is kept after the request completes.</span>
          </li>
          <li className="flex gap-3 items-start">
            <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <span>To produce your results, your request content <strong className="text-slate-900 dark:text-white font-semibold">is transmitted, transiently and over an encrypted connection, to our AI provider</strong> (Google's Gemini API). This is the one moment your content leaves your device, and it exists on our infrastructure only for the duration of the request.</span>
          </li>
          <li className="flex gap-3 items-start">
            <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <span>All of your application state - resume history, drafts, and workspace data - is stored <strong className="text-slate-900 dark:text-white font-semibold">locally on your device</strong> in your browser's storage. It is never uploaded to our database.</span>
          </li>
          <li className="flex gap-3 items-start">
            <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <span>Locally stored workspace and history data is scoped to your authenticated session, so that on a shared device your data isn't exposed to other accounts, while remaining available across sign-ins on the same device.</span>
          </li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">2. Account Information We Collect</h2>
        <p className="mb-4">
          When you sign in with Google Authentication, we store the minimum information needed to operate the service securely and prevent abuse:
        </p>
        <ul className="space-y-3 mb-6">
          <li className="flex gap-3 items-start">
            <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <span>Your <strong className="text-slate-900 dark:text-white font-semibold">email address</strong> and <strong className="text-slate-900 dark:text-white font-semibold">display name</strong> (provided via Google Auth)</span>
          </li>
          <li className="flex gap-3 items-start">
            <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <span>A <strong className="text-slate-900 dark:text-white font-semibold">unique account identifier</strong></span>
          </li>
          <li className="flex gap-3 items-start">
            <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <span><strong className="text-slate-900 dark:text-white font-semibold">Daily usage counts and timestamps</strong>, used to enforce fair-use limits</span>
          </li>
        </ul>
        <p className="mb-8">This dataset is hosted in Google Firebase and is <strong className="text-slate-900 dark:text-white font-semibold">never sold or shared with marketers, advertisers, or unaffiliated third parties</strong>. We retain it for as long as your account is active; deleting your account removes it (see Section 6).</p>
      </section>

      <section>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">3. Technical and Log Data</h2>
        <p className="mb-8">
          To keep the service secure and available, our hosting and infrastructure providers may automatically process limited technical data - such as IP addresses, browser/user-agent strings, and request timestamps - for security, abuse prevention, and rate limiting. This data is handled by our infrastructure providers under their own terms and is not used to build advertising or marketing profiles.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">4. Third-Party Services</h2>
        <p className="mb-4">
          ResumePro relies on the following third-party services, each governed by its own data processing and privacy terms:
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex gap-3 items-start bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl border border-slate-100 dark:border-slate-800">
            <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <div>
              <strong className="text-slate-900 dark:text-white font-semibold block mb-1">Google Firebase</strong>
              <span className="text-sm md:text-sm">authentication, account data storage, and application hosting.</span>
            </div>
          </li>
          <li className="flex gap-3 items-start bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl border border-slate-100 dark:border-slate-800">
            <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <div>
              <strong className="text-slate-900 dark:text-white font-semibold block mb-1">Google Gemini API</strong>
              <span className="text-sm md:text-sm">AI text generation. Your request content (resume and job text) is sent to Google for processing. Under the paid Gemini API / Vertex AI terms, this content is <strong className="text-slate-900 dark:text-white font-semibold">not used to train Google's models</strong> and is not retained for that purpose after processing.</span>
            </div>
          </li>
          <li className="flex gap-3 items-start bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl border border-slate-100 dark:border-slate-800">
            <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <div>
              <strong className="text-slate-900 dark:text-white font-semibold block mb-1">Hosting and CDN Provider</strong>
              <span className="text-sm md:text-sm">serving the application over an encrypted (HTTPS/TLS) connection.</span>
            </div>
          </li>
        </ul>
        <div className="bg-slate-100 dark:bg-slate-800/50 p-4 rounded-lg border-l-4 border-emerald-500 mb-8 text-sm italic">
          <p>Note: If any of these services processes data on servers outside your country (for example, in the United States), that constitutes an international data transfer carried out under the provider's own safeguards.</p>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">5. Cookies and Local Storage</h2>
        <p className="mb-4">
          We use cookies and local browser storage that are essential to running the application:
        </p>
        <ul className="space-y-3 mb-6">
          <li className="flex gap-3 items-start">
            <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <span><strong className="text-slate-900 dark:text-white font-semibold">Session data</strong> - keeps you logged in and securely authenticates your requests.</span>
          </li>
          <li className="flex gap-3 items-start">
            <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <span><strong className="text-slate-900 dark:text-white font-semibold">Workspace data</strong> - saves your draft resumes and job descriptions entirely on your device. This data never touches our database.</span>
          </li>
          <li className="flex gap-3 items-start">
            <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <span><strong className="text-slate-900 dark:text-white font-semibold">Preferences</strong> - remembers UI settings such as light/dark theme.</span>
          </li>
        </ul>
        <p className="mb-8">Because your workspace data lives entirely on your device, clearing your browser data or uninstalling your browser will permanently delete that data from that device.</p>
      </section>

      <section>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">6. Your Privacy Rights</h2>
        <p className="mb-4">Depending on your location (for example, under the GDPR or CCPA), you may have the right to access, export, correct, or delete your personal data, and to object to or restrict certain processing. In practice:</p>
        <ul className="space-y-3 mb-6">
          <li className="flex gap-3 items-start">
            <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <span><strong className="text-slate-900 dark:text-white font-semibold">Access and export:</strong> Because your resume content is stored on your own device, you can export it at any time directly through the app interface.</span>
          </li>
          <li className="flex gap-3 items-start">
            <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <span><strong className="text-slate-900 dark:text-white font-semibold">Deletion:</strong> You can permanently delete your account and associated usage records from the settings panel in the app. Once deleted, this cannot be recovered.</span>
          </li>
          <li className="flex gap-3 items-start">
            <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
            <span><strong className="text-slate-900 dark:text-white font-semibold">Data sent to the AI provider:</strong> Content forwarded to Google's Gemini API is processed under Google's terms; we do not hold a copy to delete.</span>
          </li>
        </ul>
        <p className="mb-8">To exercise any right not handled directly in the app, contact us using the details in Section 8.</p>
      </section>

      <section>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">7. Children's Privacy</h2>
        <p className="mb-8">
          ResumePro is not directed to children under 16 (or the minimum age required in your jurisdiction), and we do not knowingly collect data from them. If you believe a child has provided us with personal data, contact us and we will remove it.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">8. Changes and Contact</h2>
        <p className="mb-4">
          We may update this policy from time to time. Material changes will be reflected by updating the "Last updated" date above, and where appropriate we will provide a more prominent notice in the app.
        </p>
        <p>
          If you have any questions about this policy or your data, contact us at <a href="mailto:farhadrezaie73@gmail.com" className="text-emerald-600 dark:text-emerald-400 hover:underline font-medium">farhadrezaie73@gmail.com</a>.
        </p>
      </section>
    </LegalLayout>
  );
}

